import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, animate } from "framer-motion";
import { useLang, formatKickoff } from "@/i18n";
import WaitlistForm from "@/components/WaitlistForm";
import Countdown from "@/components/Countdown";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnimatedNumber = ({ value }) => {
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);
  const { lang } = useLang();

  useEffect(() => {
    if (value == null) return;
    const controls = animate(prev.current, value, {
      duration: 1.4,
      ease: "easeOut",
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    prev.current = value;
    return () => controls.stop();
  }, [value]);

  return <span data-testid="waitlist-counter-number">{display.toLocaleString(lang === "fr" ? "fr-FR" : "en-US")}</span>;
};

const MiniCrest = ({ src, label }) => {
  const [err, setErr] = useState(false);
  if (!src || err) {
    return (
      <span className="w-12 h-12 shrink-0 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[10px] font-mono2 font-bold text-zinc-400">
        {label}
      </span>
    );
  }
  return <img src={src} alt={label} data-testid={`crest-${label.toLowerCase()}`} onError={() => setErr(true)} className="w-12 h-12 shrink-0 object-contain" />;
};

export default function Hero({ today }) {
  const { lang, t } = useLang();
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/waitlist/count`)
      .then(({ data }) => setCount(data.count))
      .catch(() => setCount(106));
  }, []);

  return (
    <section data-testid="hero-section" className="relative min-h-screen pt-24 pb-14 overflow-hidden flex items-center">
      <video
        data-testid="hero-bg-video"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover object-[center_46%] opacity-50 pointer-events-none max-sm:hidden"
      >
        <source src="/intro-vortex.webm" type="video/webm" />
        <source src="/intro-vortex.mp4" type="video/mp4" />
      </video>
      <video
        data-testid="hero-bg-video-mobile"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover object-top opacity-70 pointer-events-none sm:hidden"
      >
        <source src="/intro-vortex-mobile.webm" type="video/webm" />
        <source src="/intro-vortex-mobile.mp4" type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-[#050505]/60 pointer-events-none" />
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] pointer-events-none"
        style={{ background: "radial-gradient(ellipse, rgba(204,255,0,0.08) 0%, transparent 65%)" }}
      />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#050505] to-transparent pointer-events-none" />

      <div className="max-w-2xl mx-auto px-6 relative z-10 text-center w-full">
        <motion.img
          src="/logo-hitmax.png"
          alt="HitMax"
          data-testid="hero-logo"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="h-10 sm:h-12 w-auto mx-auto mb-6 drop-shadow-[0_0_18px_rgba(0,0,0,0.6)]"
        />

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="font-mono2 text-[10px] tracking-[0.35em] uppercase text-[#CCFF00] mb-3"
        >
          {t.hero.kicker}
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="font-mono2 text-[10px] tracking-[0.35em] uppercase text-zinc-500 mb-8"
        >
          Football · Tennis · Basket
        </motion.p>

        <h1 data-testid="hero-title" className="font-display font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl mb-6">
          {t.hero.lines.map((line, i) => (
            <span key={`${lang}-${i}`} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === t.hero.lines.length - 1 ? "text-[#CCFF00]" : "text-white"}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.75 }}
          className="text-base md:text-lg text-zinc-400 leading-relaxed max-w-xl mx-auto mb-10"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="max-w-lg mx-auto text-left"
        >
          <WaitlistForm onSuccess={(c) => c && setCount(c)} />
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="mt-4 text-xs text-zinc-500"
        >
          {t.hero.trust}
        </motion.p>

        <motion.div
          data-testid="waitlist-counter"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-6 flex items-center justify-center gap-3"
        >
          <div className="flex -space-x-2">
            {["KM", "SB", "JD", "AL"].map((ini, i) => (
              <span
                key={ini}
                className="w-7 h-7 rounded-full border border-[#050505] flex items-center justify-center text-[8px] font-mono2 font-bold text-black"
                style={{ background: ["#CCFF00", "#FFC800", "#FFFFFF", "#A1A1AA"][i] }}
              >
                {ini}
              </span>
            ))}
          </div>
          <p className="text-sm text-zinc-400">
            <span className="text-white font-bold font-display"><AnimatedNumber value={count} /></span>{" "}
            {t.hero.counterSuffix}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.25 }}
          className="flex justify-center"
        >
          <Countdown />
        </motion.div>

        {today && today.match && (
          <motion.div
            data-testid="today-match-card"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.35 }}
            className="mt-8 mx-auto max-w-lg text-left rounded-md border border-[#CCFF00]/25 bg-[#0A0A0A] p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#CCFF00]">{t.today.label}</p>
              <Link
                to="/demo"
                data-testid="today-demo-link"
                className="font-mono2 text-[9px] tracking-widest uppercase text-zinc-500 hover:text-[#CCFF00] transition-colors duration-300"
              >
                {t.today.cta} →
              </Link>
            </div>
            <div className="flex items-start justify-center gap-5 mb-2">
              <div className="flex flex-col items-center gap-2 w-28">
                <MiniCrest src={today.match.homeCrest} label={today.match.homeCode} />
                <span className="font-display font-bold text-sm text-white text-center leading-tight">{today.match.home}</span>
              </div>
              <span className="text-zinc-600 font-normal font-display self-center pt-3">vs</span>
              <div className="flex flex-col items-center gap-2 w-28">
                <MiniCrest src={today.match.awayCrest} label={today.match.awayCode} />
                <span className="font-display font-bold text-sm text-white text-center leading-tight">{today.match.away}</span>
              </div>
            </div>
            <p className="font-mono2 text-[10px] tracking-widest uppercase text-zinc-500 mb-3">
              {today.match.league}
              {today.match.kickoff ? ` · ${formatKickoff(today.match.kickoff, lang)}` : ""}
            </p>
            {today.meta && (
              <div className="flex items-end justify-between gap-4 pt-3 border-t border-white/5">
                <p className="text-sm text-zinc-300">
                  {today.meta.verdict} — <span className="text-[#CCFF00] font-bold">{today.meta.probabilite}%</span>
                </p>
                <p className="font-mono2 text-[10px] tracking-widest uppercase text-zinc-500 shrink-0">
                  {t.today.odds} <span className="text-white">{today.meta.cote}</span> · {t.today.confidence}{" "}
                  <span className="text-[#CCFF00]">{today.meta.confiance}%</span>
                </p>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </section>
  );
}
